import { useEffect, useMemo, useRef, useState } from 'react'
import { SearchOption, searchStateData } from '../../../data/searchStateData'
import { SearchOptionTypes } from '../../../types/types'
import './style.scss'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { searchDataUpdated, setFilterSelectionActive, updateFilterValueCounter, updateQuickSearchValues } from '../../../redux/reducers/searchSlice'
import IconText from '../../iconText/IconText'
import CtaButton from '../../ctaButton/CtaButton'
import { instructorSearchConnector } from '../../../server/connector/InstructorSearchConnector'

interface SubFilterSectionProps {
	searchFilter: SearchOption
	index: number
}

type SubFilterListItem = {
	valueLabel: string,
	valueIndex: number
}

class SliderData {
	slidingMin: boolean = true
	parentSliderLeft: number = 0
	parentSliderWidth: number = 0
	eventX: number = 0
}

const sliderData: SliderData = new SliderData()

function SubFilterSection({searchFilter, index}: SubFilterSectionProps) {
	const dispatch = useAppDispatch()

	const subFilterSelectionActive = useAppSelector(state => state.search.subFilterSelectionActive)
	const [currentValue, setCurrentValue] = useState<number | string | number[] | undefined>(searchStateData.subFilterOptions[index].value)

	// ========================= LIST FILTER TYPE ========================================
	const [listInputValue, setListInputValue] = useState<string>('Search')
	const [listItemsArr, setListItemsArr] = useState<SubFilterListItem[]>([])

	const rangeSliderRef = useRef<HTMLDivElement>(null)
	const [isSliding, setIsSliding] = useState<boolean>(false)
	const [mouseX, setMouseX] = useState<number>(0)
	const valueString: string = currentValue === undefined ? '' : currentValue.toString()
	const initialValue: number[] = valueString.split(',').map((valueString: string) => Number(valueString))
	const [rangeMinIndex, setRangeMinIndex] = useState<number>(initialValue.length > 1 ? initialValue[0] : 0)
	const [rangeMaxIndex, setRangeMaxIndex] = useState<number>(initialValue.length > 1 ? initialValue[1] : searchFilter.valueLabels.length - 1)

	const [inputPlaceHolder, setInputPlaceHolder] = useState<string | undefined>(undefined)
	const [inputMaxPlaceHolder, setInputMaxPlaceHolder] = useState<string | undefined>(undefined)

	useEffect(() => {
		updateFilteredListArray(listInputValue)
	}, [listInputValue])

	useEffect(() => {
		setCurrentValue(searchStateData.subFilterOptions[index].value)
	}, [index, subFilterSelectionActive])

	useEffect(() => {
		dispatch(updateFilterValueCounter())
	}, [currentValue])

	const updateFilteredListArray = (newInputValue: string = '') => {
		let newItemsArr: SubFilterListItem[] = []
		searchFilter.valueLabels.forEach((label: string, index: number) => {
			if (newInputValue === '' || newInputValue === 'Search' || label.toUpperCase().includes(newInputValue.toUpperCase())) {
				newItemsArr.push({
					valueLabel: label,
					valueIndex: index
				})
			}
		})
		setListItemsArr(newItemsArr)
	}

	const listInputChange = (newValue: string) => {
		setListInputValue(newValue)
	}

	// ========================= VALUE CHANGE HANDLER ========================================
	const valueIndicatorClicked = (index: number) => {
		if (currentValue === index) {
			searchStateData.subFilterOptions[index].value = undefined
		} else {
			searchStateData.subFilterOptions[index].value = index as number
		}
		setCurrentValue(searchStateData.subFilterOptions[index].value)
		dispatch(updateQuickSearchValues())
	}

	const listValueClicked = (valueIndex: number) => {
		if (currentValue === valueIndex) {
			setCurrentValue(undefined)
			searchStateData.subFilterOptions[index].value = undefined
		} else {
			setCurrentValue(valueIndex)
			searchStateData.subFilterOptions[index].value = valueIndex
		}
	}

	// ================================== Radio Click ===================================

	const subFilterMultiRadioClickedHandler = (buttonIndex: number) => {
		if (typeof currentValue == 'object') {
			let reformedArr: number[] = [...currentValue]
			if (reformedArr.includes(buttonIndex)) {
				reformedArr.splice(reformedArr.indexOf(buttonIndex), 1)
			} else {
				reformedArr.push(buttonIndex)
			}
			setCurrentValue(reformedArr.length > 0 ? reformedArr : undefined)
			searchStateData.subFilterOptions[index].value = reformedArr.length > 0 ? reformedArr : undefined
		} else {
			setCurrentValue([buttonIndex])
			searchStateData.subFilterOptions[index].value = [buttonIndex]
		}
	}

	const subFilterSingleRadioClickedHandler = (buttonIndex: number) => {
		setCurrentValue(currentValue === buttonIndex ? undefined : buttonIndex)
		searchStateData.subFilterOptions[index].value = currentValue === buttonIndex ? undefined : buttonIndex
	}

	// ================================== Range Slider ===================================

	const onSliderTouchDownMin = (event: React.MouseEvent | React.TouchEvent) => {
		sliderData.slidingMin = true
		onSliderTouchDown(event)
	}

	const onSliderTouchDownMax = (event: React.MouseEvent | React.TouchEvent) => {
		sliderData.slidingMin = false
		onSliderTouchDown(event)
	}

	const onSliderTouchDown = (event: React.MouseEvent | React.TouchEvent) => {
		const isTouchEvent = 'touches' in event
		const boundingRect: DOMRect | undefined = event.currentTarget.parentElement?.getBoundingClientRect()
		if (boundingRect) {
			sliderData.parentSliderLeft = boundingRect.left
		}
		sliderData.parentSliderWidth = event.currentTarget.parentElement?.clientWidth ? event.currentTarget.parentElement?.clientWidth : 0

		if (isTouchEvent) {
			setMouseX(event.touches[0].clientX)
			window.addEventListener('touchend', onSliderTouchUp)
			window.addEventListener('touchmove', onSliderTouchMove)
		} else {
			setMouseX(event.clientX)
			window.addEventListener('mouseup', onSliderMouseUp)
			window.addEventListener('mousemove', onSliderMouseMove)
		}
		setIsSliding(true)
	}

	const onSliderMouseMove = (event: MouseEvent) => {
		updateSliderValue(event.clientX)
	}

	const onSliderTouchMove = (event: TouchEvent) => {
		updateSliderValue(event.touches[0].clientX)
	}

	const onSliderMouseUp = (event: MouseEvent) => {
		updateSliderValue(event.clientX)
		finishSliderValue()
		window.removeEventListener('mouseup', onSliderMouseUp)
		window.removeEventListener('mousemove', onSliderMouseMove)
		window.removeEventListener('touchend', onSliderTouchUp)
		window.removeEventListener('touchmove', onSliderTouchMove)
	}

	const onSliderTouchUp = (event: TouchEvent) => {
		if (event.changedTouches.length > 0) updateSliderValue(event.changedTouches[0].clientX)
		finishSliderValue()
		window.removeEventListener('mouseup', onSliderMouseUp)
		window.removeEventListener('mousemove', onSliderMouseMove)
		window.removeEventListener('touchend', onSliderTouchUp)
		window.removeEventListener('touchmove', onSliderTouchMove)
	}

	const updateSliderValue = (x: number) => {
		setMouseX(x)
		const { parentSliderLeft, parentSliderWidth } = sliderData
		if (!parentSliderLeft || !parentSliderWidth) return

		const segmentWidth: number = (parentSliderWidth - 30) / (searchFilter.valueLabels.length - 1)
		let closestIndex: number = 0
		
		if (x < parentSliderLeft + 15) {
			closestIndex = 0
		} else if (x > parentSliderLeft + parentSliderWidth - 15) {
			closestIndex = searchFilter.valueLabels.length - 1
		} else {
			closestIndex = Math.floor(((x + (segmentWidth / 2)) - (parentSliderLeft + 15)) / segmentWidth)
		}

		if (sliderData.slidingMin) {
			let minIndex: number = 0
			if (closestIndex > rangeMaxIndex - 1) {
				minIndex = rangeMaxIndex - 1
			} else if (closestIndex < 1) {
				minIndex = 0
			} else {
				minIndex = closestIndex
			}
			setRangeMinIndex(minIndex)
		} else {
			let maxIndex: number = 0
			if (closestIndex < rangeMinIndex + 1) {
				maxIndex = rangeMinIndex + 1
			} else if (closestIndex > searchFilter.valueLabels.length - 1) {
				maxIndex = searchFilter.valueLabels.length - 1
			} else {
				maxIndex = closestIndex
			}
			setRangeMaxIndex(maxIndex)
		}
	}

	const finishSliderValue = () => {
		setIsSliding(false)
	}

	const sliderMinStyle = useMemo(
		() => ({
			marginLeft: isSliding && sliderData.slidingMin ? mouseX - sliderData.parentSliderLeft - 15 : `calc(((100% - 30px)/${searchFilter.valueLabels.length-1})*${rangeMinIndex})`,
			background: isSliding && sliderData.slidingMin ? '#002da6' : '#2A50BB'
		}),
		[rangeMinIndex, isSliding, mouseX]
	)

	const sliderMaxStyle = useMemo(
		() => ({
			marginRight: isSliding && !sliderData.slidingMin ? sliderData.parentSliderLeft + sliderData.parentSliderWidth - mouseX - 15 : `calc(((100% - 30px)/${searchFilter.valueLabels.length-1})*${(searchFilter.valueLabels.length - 1) - (rangeMaxIndex ? rangeMaxIndex : searchFilter.valueLabels.length-1)})`,
			background: isSliding && !sliderData.slidingMin ? '#002da6' : '#2A50BB'
		}),
		[rangeMaxIndex, isSliding, mouseX]
	)

	// ================================== Other ===================================

	const clearValue = () => {
		if (searchFilter.optionType === SearchOptionTypes.RANGE) {
			setRangeMinIndex(0)
			setRangeMaxIndex(searchFilter.valueLabels.length - 1)
		}
		searchStateData.subFilterOptions[index].value = undefined
		setCurrentValue(undefined)
		// showInstructorsClickHandler()
		searchStateData.clearExistingProfiles()
		instructorSearchConnector.performSearchRequest().then((successRequest) => {
			dispatch(searchDataUpdated())
			if (!successRequest) {
				console.log('WARNING - [App] - performSearchRequest() failed')
			}
		})
		dispatch(setFilterSelectionActive(false))
	}

	// ================================== Set value on close ===================================

	const showInstructorsClickHandler = () => {
		let shouldPerformNewSearch: boolean = false
		if (searchFilter.optionType === SearchOptionTypes.RANGE) {
			if (rangeMinIndex === 0 && rangeMaxIndex === searchFilter.valueLabels.length - 1) {
				if (searchStateData.subFilterOptions[index].value !== undefined) {
					shouldPerformNewSearch = true
				}
				searchStateData.subFilterOptions[index].value = undefined
			} else {
				if (initialValue.length > 1) {
					if (initialValue[0] !== rangeMinIndex || initialValue[1] !== rangeMaxIndex) {
						shouldPerformNewSearch = true
					}
				} else {
					shouldPerformNewSearch = true
				}
				searchStateData.subFilterOptions[index].value = [rangeMinIndex, rangeMaxIndex]
			}
		} else {
			if (currentValue === undefined) {
				// if (searchStateData.subFilterOptions[index].value !== undefined) {
				// 	shouldPerformNewSearch = true
				// }
				shouldPerformNewSearch = true
				searchStateData.subFilterOptions[index].value = undefined	
			} else {
				// if (searchStateData.subFilterOptions[index].value !== currentValue) {
				// 	shouldPerformNewSearch = true
				// }
				shouldPerformNewSearch = true
				searchStateData.subFilterOptions[index].value = currentValue as number
			}
		}
		if (shouldPerformNewSearch) {
			searchStateData.clearExistingProfiles()
			instructorSearchConnector.performSearchRequest().then((successRequest) => {
				dispatch(searchDataUpdated())
				if (!successRequest) {
					console.log('WARNING - [App] - performSearchRequest() failed')
				}
			})
		}
		dispatch(setFilterSelectionActive(false))
	}

	return (
		<div className='sub-filter-section-wrap'>
			<div className="sub-filter-section-label">
				{searchFilter.label}
			</div>
			{searchFilter.optionType === SearchOptionTypes.LIST && (
				<div className='sub-filter-section-value-list-wrap'>
					<input 
						className='sub-filter-section-list-input'
						type='text'
						value={listInputValue}
						onChange={(evt) => {
							listInputChange(evt.target.value)
							// setListInputValue(evt.target.value)
						}}
						onFocus={() => {
							if (listInputValue === 'Search') {
								setListInputValue('')
							}
						}}
						onBlur={() => {
							if (listInputValue === '') {
								setListInputValue('Search')
							}
						}}
					/>
					{currentValue !== undefined && (
						<div
							className={`sub-filter-section-value-list-item selected`}
							onClick={() => clearValue()}
						>
							<div className='sub-filter-section-value-list-item-icon-wrap'>
								<img 
									src={`/imgs/icons/icon-language-list.svg`} 
									className='sub-filter-section-value-list-item-icon'
									alt='Language'
								/>
							</div>
							{searchFilter.valueLabels[currentValue as number]}
						</div>
					)}
					<div
						className='sub-filter-section-list-item-wrap'
					>
						{listItemsArr.map((value: SubFilterListItem, index) => 
							<div
								className={`sub-filter-section-value-list-item ${currentValue === value.valueIndex ? 'selected' : ''}`}
								key={`selectValueLabel${index}`}
								onClick={() => listValueClicked(value.valueIndex as number)}
							>
								<div className='sub-filter-section-value-list-item-icon-wrap'>
									<img 
										src={`/imgs/icons/icon-language-list.svg`} 
										className='sub-filter-section-value-list-item-icon'
										alt='Language'
									/>
								</div>
								{value.valueLabel}
							</div>
						)}
					</div>
				</div>
			)}
			{searchFilter.optionType === SearchOptionTypes.SINGLE_FROM_RANGE && (
				<>
					<div className='sub-filter-section-value-label-wrap'>
						{searchFilter.valueLabels.map((value: string, index) => 
							<div className='sub-filter-section-value-label' key={`selectValueLabel${index}`}>{value}</div>
						)}
					</div>
					<div className='sub-filter-section-value-indicator-wrap'>
						{searchFilter.valueLabels.map((label, index) => 
							<div 
								className={`sub-filter-section-value-indicator ${searchFilter.value === index ? 'selected' : ''}`}
								key={`select-value-indicator-${label}${index}`}
								onClick={() => valueIndicatorClicked(index)}
							/>
						)}
					</div>
				</>
			)}
			{searchFilter.optionType === SearchOptionTypes.RANGE && (
				<>
					<div 
						className='sub-filter-section-range-wrap'
						ref={rangeSliderRef}
					>
						<div 
							className={`sub-filter-section-range-indicator`}
							style={sliderMinStyle}
							onMouseDown={onSliderTouchDownMin}
							onTouchStart={onSliderTouchDownMin}
						>
							<div className='sub-filter-section-range-indicator-value'>{searchFilter.valueLabels[rangeMinIndex ? rangeMinIndex : 0]}</div>
						</div>
						<div 
							className={`sub-filter-section-range-indicator-seperator`}
							// style={sliderBarStyle}
						/>
						<div 
							className={`sub-filter-section-range-indicator`}
							style={sliderMaxStyle}
							onMouseDown={onSliderTouchDownMax}
							onTouchStart={onSliderTouchDownMax}
						>
							<div className='sub-filter-section-range-indicator-value'>{searchFilter.valueLabels[rangeMaxIndex ? rangeMaxIndex : searchFilter.valueLabels.length - 1]}</div>
						</div>
					</div>
					<div className='sub-filter-section-value-label-wrap'>
						{searchFilter.valueLabels.map((value: string, index) => 
							<div className='sub-filter-section-value-label' key={`selectValueLabel${index}`}>{value}</div>
						)}
					</div>
					<div className='sub-filter-section-min-max-wrap'>
						<div className='sub-filter-section-min-max-value-wrap'>
							<div className='sub-filter-section-min-max-value-label'>Minimum</div>
							<div className='sub-filter-section-min-max-value-value'>
								{/* <div className='m-a'>{searchFilter.valueLabels[rangeMinIndex ? rangeMinIndex : 0]}</div> */}
								<input
									className='sub-filter-section-min-max-value-input'
									value={inputPlaceHolder ? inputPlaceHolder : searchFilter.valueLabels[rangeMinIndex ? rangeMinIndex : 0]}
									onChange={(evt) => {
										// let valueParsed: number = parseInt(evt.target.value)
										setInputPlaceHolder(evt.target.value)
										if (searchFilter.valueLabels.includes(evt.target.value)) {
											setRangeMinIndex(searchFilter.valueLabels.indexOf(evt.target.value))
										}
									}}
									onBlur={() => {
										setInputPlaceHolder(undefined)
									}}
								/>
							</div>
						</div>
						<div className='sub-filter-section-min-max-value-wrap'>
							<div className='sub-filter-section-min-max-value-label'>Maximum</div>
							<div className='sub-filter-section-min-max-value-value'>
								{/* <div className='m-a'>{searchFilter.valueLabels[rangeMaxIndex ? rangeMaxIndex : searchFilter.valueLabels.length - 1]}</div> */}
								<input
									className='sub-filter-section-min-max-value-input'
									value={inputMaxPlaceHolder ? inputMaxPlaceHolder : searchFilter.valueLabels[rangeMaxIndex ? rangeMaxIndex : searchFilter.valueLabels.length - 1]}
									onChange={(evt) => {
										// let valueParsed: number = parseInt(evt.target.value)
										setInputMaxPlaceHolder(evt.target.value)
										if (searchFilter.valueLabels.includes(evt.target.value)) {
											setRangeMaxIndex(searchFilter.valueLabels.indexOf(evt.target.value))
										}
									}}
									onBlur={() => {
										setInputMaxPlaceHolder(undefined)
									}}
								/>
							</div>
						</div>
					</div>
				</>
			)}
			{searchFilter.optionType === SearchOptionTypes.MULTI_LIST && (
				<div className='sub-filter-section-radio-section-wrap'>
					{searchFilter.valueLabels.map((value, index) => 
						<div 
							className='sub-filter-section-radio-wrap'
							key={`subFilterRadio${index}`}
						>
							<label>
								<input 
									className='sub-filter-section-radio-button'
									type='radio' 
									checked={typeof currentValue == 'object' && currentValue.includes(index)} 
									onClick={() => {
										subFilterMultiRadioClickedHandler(index)
									}}
									readOnly={true}
								/>
								<span/><IconText text={value} />
							</label>
							
						</div>
					)}
				</div>
			)}
			{searchFilter.optionType === SearchOptionTypes.SINGLE_LIST && (
				<div className='sub-filter-section-radio-section-wrap'>
					{searchFilter.valueLabels.map((value, index) => 
						<div 
							className='sub-filter-section-radio-wrap'
							key={`subFilterRadio${index}`}
						>
							<label>
								<input 
									className='sub-filter-section-radio-button'
									type='radio' 
									checked={currentValue === index}
									onClick={() => {
										subFilterSingleRadioClickedHandler(index)
									}}
									readOnly={true}
								/>
								<span/><IconText text={value} />
							</label>
						</div>
					)}
				</div>
			)}
			<div className='sub-filter-section-action-wrap'>
				<div 
					className='sub-filter-section-action-clear-all-button'
					onClick={clearValue}
				>
					Clear All <img src='/imgs/icons/icon-small-close.svg' alt='Small close' />
				</div>
				<CtaButton
					label='Show Instructors'
					size='medium'
					onClick={() => { showInstructorsClickHandler() }}
				/>
			</div>
		</div>
	)
}

export default SubFilterSection
